import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Button,
  Modal,
  Select,
  Input,
  Form,
  Card,
  Row,
  Col,
  Statistic,
  Space,
  Typography,
  Divider,
  message as antMessage,
} from 'antd';
import {
  CustomerServiceOutlined,
  ReloadOutlined,
  SearchOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  UserOutlined,
  ShopOutlined,
  MailOutlined,
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { useSelector } from 'react-redux';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const statusColorMap: Record<string, { color: string; icon: React.ReactNode }> = {
  OPEN: { color: 'error', icon: <ClockCircleOutlined /> },
  IN_PROGRESS: { color: 'processing', icon: <SyncOutlined spin /> },
  RESOLVED: { color: 'success', icon: <CheckCircleOutlined /> },
  CLOSED: { color: 'default', icon: <CloseCircleOutlined /> },
};

const SupportTickets: React.FC = () => {
  const userDetail = useSelector((state: any) => state.UserData?.userData);
  const isSuperAdmin = userDetail?.role === 'superAdmin';

  const [loading, setLoading] = useState<boolean>(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [searchText, setSearchText] = useState<string>('');

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [form] = Form.useForm();

  // Fetch Stores for SuperAdmin filter
  const fetchStores = async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await ApiServices('GET', ApiConfig.STORES);
      if (res?.data) {
        setStores(res.data);
      }
    } catch (e) {
      console.error('Error fetching stores:', e);
    }
  };

  // Fetch Tickets from API
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(page));
      queryParams.append('limit', String(limit));
      if (selectedStatus && selectedStatus !== 'ALL') {
        queryParams.append('status', selectedStatus);
      }
      if (isSuperAdmin && selectedStoreId && selectedStoreId !== 'ALL') {
        queryParams.append('storeId', selectedStoreId);
      }
      if (searchText) {
        queryParams.append('search', searchText);
      }

      const url = `${ApiConfig.SUPPORT_TICKETS}?${queryParams.toString()}`;
      const res = await ApiServices('GET', url);

      if (res?.success) {
        setTickets(res.data || []);
        setTotalCount(res.totalCount || 0);
      } else {
        antMessage.error(res?.message || 'Failed to load support tickets');
      }
    } catch (err: any) {
      console.error('Error fetching support tickets:', err);
      antMessage.error('Error connecting to support service');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchTickets();
  }, [page, limit, selectedStoreId, selectedStatus]);

  const handleSearch = () => {
    setPage(1);
    fetchTickets();
  };

  const handleResetFilters = () => {
    setSelectedStoreId('ALL');
    setSelectedStatus('ALL');
    setSearchText('');
    setPage(1);
  };

  const openManageModal = (ticket: any) => {
    setSelectedTicket(ticket);
    form.setFieldsValue({
      status: ticket.status,
      reply: ticket.reply || '',
    });
    setIsModalOpen(true);
  };

  const handleSaveTicket = async (values: any) => {
    if (!selectedTicket) return;
    setSubmitting(true);
    try {
      const url = `${ApiConfig.SUPPORT_TICKETS}/${selectedTicket.id}`;
      const res = await ApiServices('PUT', url, {
        status: values.status,
        reply: values.reply,
      });

      if (res?.success) {
        antMessage.success('Support ticket updated successfully');
        setIsModalOpen(false);
        fetchTickets();
      } else {
        antMessage.error(res?.message || 'Failed to update support ticket');
      }
    } catch (err: any) {
      console.error('Error updating ticket:', err);
      antMessage.error('Failed to update support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  // Stats calculation
  const openCount = tickets.filter((t) => t.status === 'OPEN').length;
  const inProgressCount = tickets.filter((t) => t.status === 'IN_PROGRESS').length;
  const resolvedCount = tickets.filter((t) => t.status === 'RESOLVED').length;

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: number) => <Text style={{ fontWeight: 'bold', color: '#1890ff' }}>#ST-{id}</Text>,
    },
    {
      title: 'Submitter / Driver',
      key: 'submitter',
      render: (record: any) => (
        <Space direction="vertical" size={2}>
          <Space>
            <UserOutlined style={{ color: '#4b5563' }} />
            <Text strong>{record.name || 'Anonymous'}</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <MailOutlined style={{ marginRight: 4 }} />
            {record.email}
          </Text>
          {record.driver && (
            <Tag color="blue" style={{ marginTop: 2 }}>
              Driver: {record.driver.firstName} {record.driver.lastName}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Store',
      key: 'store',
      render: (record: any) => {
        const storeName = record.store?.name || 'Default Store';
        return (
          <Space>
            <ShopOutlined style={{ color: '#0700d6' }} />
            <Text>{storeName}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Subject & Message',
      key: 'subject',
      render: (record: any) => (
        <div style={{ maxWidth: 320 }}>
          <Text strong style={{ display: 'block', marginBottom: 2 }}>
            {record.subject || 'Support Request'}
          </Text>
          <Paragraph
            ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
            style={{ color: '#6b7280', fontSize: 12, marginBottom: 0 }}
          >
            {record.message}
          </Paragraph>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => {
        const cfg = statusColorMap[status] || { color: 'default', icon: null };
        return (
          <Tag color={cfg.color} icon={cfg.icon} style={{ borderRadius: 6, padding: '2px 8px' }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Submitted At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => (date ? moment(date).format('DD MMM YYYY, hh:mm A') : '—'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (record: any) => (
        <Button
          type="primary"
          ghost
          icon={<MessageOutlined />}
          onClick={() => openManageModal(record)}
          size="small"
        >
          Manage
        </Button>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: 24 }}>
        {/* Header Title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#111827' }}>
              <CustomerServiceOutlined style={{ marginRight: 10, color: '#0700d6' }} />
              Contact & Support Management
            </Title>
            <Text type="secondary">Manage driver inquiries and support requests store-wise</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={fetchTickets} loading={loading}>
            Refresh
          </Button>
        </div>

        {/* Overview Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #1890ff' }}>
              <Statistic title="Total Support Tickets" value={totalCount} prefix={<MessageOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #ff4d4f' }}>
              <Statistic title="Open Tickets" value={openCount} valueStyle={{ color: '#ff4d4f' }} prefix={<ClockCircleOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #faad14' }}>
              <Statistic title="In Progress" value={inProgressCount} valueStyle={{ color: '#faad14' }} prefix={<SyncOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ borderRadius: 12, borderLeft: '4px solid #52c41a' }}>
              <Statistic title="Resolved Tickets" value={resolvedCount} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* Filter Controls Bar */}
        <Card style={{ marginBottom: 24, borderRadius: 12 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Search */}
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by name, email, or message..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
              />
            </Col>

            {/* Store Filter (SuperAdmin) */}
            {isSuperAdmin && (
              <Col xs={24} sm={12} md={6}>
                <Select
                  style={{ width: '100%' }}
                  placeholder="Filter by Store"
                  value={selectedStoreId}
                  onChange={(val) => {
                    setSelectedStoreId(val);
                    setPage(1);
                  }}
                >
                  <Option value="ALL">All Stores</Option>
                  {stores.map((s) => (
                    <Option key={s.id} value={String(s.id)}>
                      {s.name} ({s.shop})
                    </Option>
                  ))}
                </Select>
              </Col>
            )}

            {/* Status Filter */}
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by Status"
                value={selectedStatus}
                onChange={(val) => {
                  setSelectedStatus(val);
                  setPage(1);
                }}
              >
                <Option value="ALL">All Statuses</Option>
                <Option value="OPEN">OPEN</Option>
                <Option value="IN_PROGRESS">IN_PROGRESS</Option>
                <Option value="RESOLVED">RESOLVED</Option>
                <Option value="CLOSED">CLOSED</Option>
              </Select>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={12} md={4} style={{ textAlign: 'right' }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Search
                </Button>
                <Button onClick={handleResetFilters}>Reset</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tickets Data Table */}
        <Card style={{ borderRadius: 12 }}>
          <Table
            columns={columns}
            dataSource={tickets}
            rowKey="id"
            loading={loading}
            pagination={{
              current: page,
              pageSize: limit,
              total: totalCount,
              onChange: (p, pSize) => {
                setPage(p);
                setLimit(pSize);
              },
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} support tickets`,
            }}
          />
        </Card>

        {/* Manage & Reply Ticket Modal */}
        <Modal
          title={
            <Space>
              <CustomerServiceOutlined style={{ color: '#0700d6' }} />
              <span>Manage Support Ticket #{selectedTicket?.id}</span>
            </Space>
          }
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={650}
          destroyOnClose
        >
          {selectedTicket && (
            <div>
              {/* Ticket Meta Info */}
              <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                <Row gutter={[16, 12]}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Submitted By</Text>
                    <Text strong>{selectedTicket.name}</Text> ({selectedTicket.email})
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Store Name</Text>
                    <Text strong>{selectedTicket.store?.name || 'Default Store'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Subject</Text>
                    <Text strong>{selectedTicket.subject || 'Support Request'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Date Submitted</Text>
                    <Text>{moment(selectedTicket.created_at).format('DD MMM YYYY, hh:mm A')}</Text>
                  </Col>
                </Row>
              </div>

              {/* Original Message */}
              <div style={{ marginBottom: 20 }}>
                <Text strong style={{ display: 'block', marginBottom: 6 }}>Message Body:</Text>
                <div style={{ background: '#ffffff', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb', whiteSpace: 'pre-wrap' }}>
                  {selectedTicket.message}
                </div>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              {/* Response & Status Form */}
              <Form form={form} layout="vertical" onFinish={handleSaveTicket}>
                <Form.Item
                  name="status"
                  label="Update Ticket Status"
                  rules={[{ required: true, message: 'Please select a status' }]}
                >
                  <Select>
                    <Option value="OPEN">
                      <Tag color="error">OPEN</Tag> Ticket is waiting for review
                    </Option>
                    <Option value="IN_PROGRESS">
                      <Tag color="processing">IN_PROGRESS</Tag> Under active investigation
                    </Option>
                    <Option value="RESOLVED">
                      <Tag color="success">RESOLVED</Tag> Support issue has been solved
                    </Option>
                    <Option value="CLOSED">
                      <Tag color="default">CLOSED</Tag> Ticket closed
                    </Option>
                  </Select>
                </Form.Item>

                <Form.Item name="reply" label="Admin Reply / Resolution Notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter resolution notes or message to send back to the driver..."
                  />
                </Form.Item>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                  <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
                  <Button type="primary" htmlType="submit" loading={submitting} icon={<CheckCircleOutlined />}>
                    Save Changes
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default SupportTickets;
